import { Css, FormSectionLayout, useComputed, useModal } from "@homebound/beam";
import { type ObjectConfig, useFormState } from "@homebound/form-state";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  type AiProposalsFormValue,
  aiProposalsConfig,
  flattenEntityProposals,
  hasPendingProposals,
  indexEntityProposals,
  toResolveInputs,
} from "src/components/formFields/ai/utils/aiProposalForm";
import {
  reconcilePendingAiProposals,
  resolvePendingAiProposals,
} from "src/components/formFields/ai/utils/reconcilePendingAiProposals";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { AiFieldProposalStatus, FeatureFlagType, type SaveReadyPlanLocationInput } from "src/generated/graphqlTypes";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import {
  PlanPackageLocationProposalStatusFragmentDoc,
  type PlanPackageLocationsV2Query,
  type PlanPackageLocationsV2_PlanPackageFragment,
  usePlanPackageLocationsV2Query,
  useSavePlanPackageLocationsV2Mutation,
} from "src/routes/libraries/plan-package-v2/components/LocationsTab.generated";
import { PlanPackageAiReviewBanner } from "src/routes/libraries/plan-package-v2/components/PlanPackageAiReviewBanner";
import { AddLocationModal } from "src/routes/libraries/plan-package-v2/components/locations/AddLocationModal";
import { LevelLocationsTable } from "src/routes/libraries/plan-package-v2/components/locations/LevelLocationsTable";
import {
  LocationRow,
  type LocationRowForm,
  type LocationRowInput,
  locationRowConfig,
} from "src/routes/libraries/plan-package-v2/components/locations/LocationRow";
import { roomProposalBindings } from "src/routes/libraries/plan-package-v2/components/locations/locationProposalUtils";
import {
  type LevelGroup,
  type RoomFormValue,
  formatFullRoomNumber,
  groupRoomLocations,
  isNumberedLevel,
} from "src/routes/libraries/plan-package-v2/components/locations/locationUtils";
import { queryResult } from "src/utils";

type LocationsTabProps = { planPackageId: string; versionId: string };
type LocationsFormValue = AiProposalsFormValue & { rooms: RoomFormValue[] };

export function LocationsTab(props: LocationsTabProps) {
  const { planPackageId, versionId } = props;
  const query = usePlanPackageLocationsV2Query({ variables: { id: planPackageId, versionId } });
  return queryResult(query, ({ planPackage, levels, roomTypes }) => (
    <DataView planPackage={planPackage} levels={levels} roomTypes={roomTypes} refetch={() => query.refetch()} />
  ));
}

type DataViewProps = {
  planPackage: PlanPackageLocationsV2_PlanPackageFragment;
  levels: PlanPackageLocationsV2Query["levels"];
  roomTypes: PlanPackageLocationsV2Query["roomTypes"];
  refetch: ReturnType<typeof usePlanPackageLocationsV2Query>["refetch"];
};

function DataView(props: DataViewProps) {
  const { planPackage, levels, roomTypes, refetch } = props;
  const [save] = useSavePlanPackageLocationsV2Mutation();
  const navigateToRpav = useNavigateToRpav();
  const { openModal } = useModal();
  const aiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesAi);
  const failedRoomProposals = useRef(new Map<string, string[]>());

  const groups = useMemo(() => groupRoomLocations(planPackage.roomLocations), [planPackage]);
  // Our import creates a CREATE proposal's RPL up-front, so every proposal is keyed by an existing room.
  const roomProposals = useMemo(
    () => indexEntityProposals(aiEnabled ? planPackage.aiProposal?.entityProposals : undefined, "ReadyPlanLocation"),
    [planPackage, aiEnabled],
  );

  /**
   * Saves rooms and their proposal statuses atomically, then refreshes membership when needed.
   * Acknowledges this request while keeping newer edits dirty for the next autosave.
   */
  async function saveRpls(inputs: SaveReadyPlanLocationInput[], opts: { membershipChanged?: boolean } = {}) {
    const statuses = inputs.flatMap((input) => input.proposals ?? []);
    const { data } = await save({
      variables: { inputs },
      update: (cache, result) => {
        if (!result.data) return;
        const savedRooms = result.data.saveReadyPlanLocations.readyPlanLocations;
        // Resolutions are soft-deleted and may be absent from the returned proposal tree. A successful
        // atomic write acknowledges these exact statuses, not any edits made while the request ran.
        locationsForm.update({
          rooms: locationsForm.rooms.rows.map((row) => {
            const saved = savedRooms.find((saved) => saved.id === row.id.value);
            return saved
              ? {
                  id: saved.id,
                  roomTypeId: saved.roomType?.id,
                  roomNumber: saved.roomNumber,
                  marketingName: saved.marketingName,
                }
              : row.originalValue;
          }),
          fieldProposals: locationsForm.fieldProposals.rows.map((row) => ({
            ...row.originalValue,
            status: statuses.find((status) => status.id === row.id.value)?.status ?? row.status.originalValue,
          })),
        });
        statuses.forEach((proposal) => {
          cache.writeFragment({
            id: cache.identify({ __typename: "AiFieldProposal", id: proposal.id }),
            fragment: PlanPackageLocationProposalStatusFragmentDoc,
            data: {
              __typename: "AiFieldProposal",
              id: proposal.id,
              status: { __typename: "AiFieldProposalStatusDetail", code: proposal.status },
            },
          });
        });
      },
    }).catch((error: unknown) => {
      // Restore only this failed write, not a newer decision made while it was in flight.
      inputs.forEach((input) => {
        const restored = (input.proposals ?? [])
          .filter(
            (s) => locationsForm.fieldProposals.rows.find((row) => row.id.value === s.id)?.status.value === s.status,
          )
          .map((s) => s.id);
        if (input.id && restored.nonEmpty) failedRoomProposals.current.set(input.id, restored);
        restoreStoredProposalStatuses(restored);
      });
      throw error;
    });
    if (!data) throw new Error("Location save returned no data");
    inputs.forEach((input) => {
      if (input.id) failedRoomProposals.current.delete(input.id);
    });
    // Deleted rooms have no server row to acknowledge their buffered edits. Discard those edits only
    // after the delete succeeds so the membership refresh can remove their now-clean form rows.
    inputs
      .filter((input) => input.delete)
      .forEach((input) => {
        locationsForm.rooms.rows.find((row) => row.id.value === input.id)?.revertChanges();
      });
    const draftId = data.saveReadyPlanLocations.readyPlans.first?.aggregateDraft?.id;
    if (draftId && draftId !== planPackage.version.id) return navigateToRpav(draftId);
    if (opts.membershipChanged) {
      const { data: fresh } = await refetch();
      const freshDraftId = fresh?.planPackage?.aggregateDraft?.id;
      if (freshDraftId && freshDraftId !== planPackage.version.id) navigateToRpav(freshDraftId);
    }
  }

  const roomInputs = useMemo(() => {
    const rooms = groups.flatMap((group) =>
      group.rooms.map((rpl) => ({ rplId: rpl.id, levelName: group.location.name, fullRoomNumber: rpl.fullRoomNumber })),
    );
    return groups.flatMap((group) =>
      group.rooms.map((rpl): LocationRowInput => ({
        rpl,
        levelName: group.location.name,
        proposal: roomProposals.get(rpl.id),
        fieldProposals: [],
        rooms,
      })),
    );
  }, [groups, roomProposals]);
  const formInput = useMemo<LocationsFormValue>(
    () => ({
      rooms: roomInputs.map((input) => new LocationRow(input)),
      fieldProposals: flattenEntityProposals([...roomProposals.values()]),
    }),
    [roomInputs, roomProposals],
  );

  // One owner batches room edits and their statuses at the end of the event, and queues edits made in flight.
  const locationsForm = useFormState({
    config: locationsConfig,
    init: { input: formInput },
    autoSave: async (state) => {
      if (!state.canSave()) return;
      const statuses = toResolveInputs(state.fieldProposals.changedValue);
      const inputs = state.rooms.rows.flatMap((os): SaveReadyPlanLocationInput[] => {
        const room = os.value as LocationRow;
        const proposals = statuses.filter((status) => room.fieldProposals.some((fp) => fp.id.value === status.id));
        // A queued edit on another room must not retry failed values without their restored decisions.
        const failed = failedRoomProposals.current.get(room.id) ?? [];
        if (failed.some((id) => !proposals.some((proposal) => proposal.id === id))) return [];
        if (!os.dirty && proposals.isEmpty) return [];
        const changed = os.changedValue;
        return [
          {
            id: changed.id,
            // A fully-ignored CREATE, or a fully-accepted DELETE, means the room itself goes away.
            ...(proposals.nonEmpty && room.needsDelete
              ? { delete: true }
              : {
                  // Keep the wire payload writable-only even if the form config later gains metadata.
                  ...("roomTypeId" in changed ? { roomTypeId: changed.roomTypeId } : {}),
                  ...("marketingName" in changed ? { marketingName: changed.marketingName } : {}),
                  // The backend recalcs the room's Location from roomNumber whenever roomType changes.
                  ...("roomTypeId" in changed || "roomNumber" in changed
                    ? { roomNumber: "roomTypeId" in changed ? os.roomNumber.value : changed.roomNumber }
                    : {}),
                }),
            ...(proposals.nonEmpty ? { proposals } : {}),
          },
        ];
      });
      if (inputs.isEmpty) return;
      try {
        await saveRpls(inputs, { membershipChanged: inputs.some((input) => input.delete) });
      } catch {
        // Apollo already shows the error; rethrowing would escape the auto-save timer.
      }
    },
  });

  /** What's actually stored server-side for each proposal, as of the last load — independent of any optimistic local flip. */
  const storedProposalStatuses = useMemo(
    () =>
      new Map(
        [...roomProposals.values()].flatMap((p) => p.fieldProposals.map((fp) => [fp.id, fp.status.code] as const)),
      ),
    [roomProposals],
  );

  /** Puts a failed batch's statuses back, so its proposals come back up for review. */
  function restoreStoredProposalStatuses(ids: string[]): void {
    ids.forEach((id) => {
      const row = locationsForm.fieldProposals.rows.find((r) => r.id.value === id);
      // A previously acknowledged proposal may now be absent from the backend's soft-delete-filtered tree.
      const status = storedProposalStatuses.get(id) ?? row?.status.originalValue;
      if (row && status) row.status.set(status);
    });
  }

  // Attach live proposal states, not snapshots, so cells and row actions share the tab-level form.
  const fieldsById = useComputed(
    () => new Map(locationsForm.fieldProposals.rows.map((row) => [row.id.value, row])),
    [locationsForm],
  );
  const roomsById = useComputed(
    () => new Map(locationsForm.rooms.rows.map((row) => [row.id.value, row as LocationRowForm])),
    [locationsForm],
  );
  const [tableGroups, setTableGroups] = useState<(Omit<LevelGroup, "rooms"> & { rooms: LocationRowForm[] })[]>([]);
  // Materialize and refresh forms before paint, never from table computeds or cell renderers.
  useLayoutEffect(() => {
    setTableGroups(
      groups.map((group) => ({
        ...group,
        rooms: group.rooms.flatMap((rpl) => {
          const os = roomsById.get(rpl.id);
          if (!os) return [];
          // Resolved proposals disappear upstream. Keep their ownership while a newer decision is dirty.
          const proposal =
            roomProposals.get(rpl.id) ??
            (os.value.fieldProposals.some((fp) => fp.dirty) ? os.value.proposal : undefined);
          const input = {
            ...roomInputs.find((input) => input.rpl.id === rpl.id)!,
            proposal,
            fieldProposals: [
              ...(proposal?.fieldProposals ?? []).map((fp) => fp.id),
              ...os.value.fieldProposals.filter((fp) => fp.dirty).map((fp) => fp.id.value),
            ]
              .unique()
              .map((id) => fieldsById.get(id))
              .compact(),
          };
          // form-state preserves the backing class, while its config exposes only writable fields.
          os.value.update(input);
          return os;
        }),
      })),
    );
  }, [groups, roomProposals, fieldsById, roomsById, roomInputs]);

  const disabled = disableBasedOnPotentialOperation(planPackage.canEdit);
  const lastNumberedGroup = tableGroups.filter((group) => isNumberedLevel(group.location.name)).last;

  function isDuplicateFullRoomNumber(rplId: string, fullRoomNumber: number): boolean {
    return tableGroups.some(({ location, rooms }) =>
      rooms.some(
        (room) =>
          room.value.id !== rplId && formatFullRoomNumber(location.name, room.roomNumber.value ?? 0) === fullRoomNumber,
      ),
    );
  }

  // Derived from the row warnings so the banner's count and each row's banner always agree.
  // Both read the live proposal statuses out of `locationsForm`, so a warning clears the moment its
  // proposal is resolved and the banner clears on Accept All / Ignore All — no refetch needed.
  // Room forms are already materialized before these computed reads.
  const warnings = useComputed(
    () => tableGroups.flatMap((group) => group.rooms.map((os) => os.value.warning).compact()),
    [tableGroups],
  );
  const hasPending = useComputed(() => hasPendingProposals(locationsForm.fieldProposals.rows), [locationsForm]);

  /** Accept All / Ignore All, which `locationsForm`'s auto-save then persists. */
  function reconcileAll(decision: AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected) {
    tableGroups.forEach(({ location, rooms }) =>
      rooms.forEach((os) => {
        const { fieldProposals } = os.value;
        reconcilePendingAiProposals(roomProposalBindings(os, location.name, fieldProposals, roomTypes), decision);
        resolvePendingAiProposals(fieldProposals, decision);
      }),
    );
  }

  return (
    <>
      {aiEnabled && hasPending && (
        <PlanPackageAiReviewBanner hasPending={hasPending} warnings={warnings.length} onReconcileAll={reconcileAll} />
      )}
      <FormSectionLayout
        title="Locations"
        description="Add the levels and rooms of this plan, and keep room numbers in sync with the construction documents."
        actions={[
          {
            label: "Add",
            icon: "plus",
            variant: "secondary",
            disabled,
            onClick: () =>
              openModal({
                content: (
                  <AddLocationModal
                    planPackageId={planPackage.id}
                    groups={groups}
                    levels={levels}
                    roomTypes={roomTypes}
                    saveRpls={saveRpls}
                  />
                ),
              }),
          },
        ]}
        initialFields={
          <div css={Css.df.fdc.gap3.pb8.w100.$}>
            {groups.isEmpty && <div>No locations found</div>}
            {tableGroups.map((group) => (
              <LevelLocationsTable
                key={group.location.id}
                group={group}
                roomTypes={roomTypes}
                disabled={disabled}
                saveRpls={saveRpls}
                isDuplicateFullRoomNumber={isDuplicateFullRoomNumber}
                // Levels 1 and 2 can't be deleted while Level 3 exists, so only offer the last numbered level
                showLevelDelete={
                  !!group.levelRpl && (!isNumberedLevel(group.location.name) || group === lastNumberedGroup)
                }
              />
            ))}
          </div>
        }
      />
    </>
  );
}

const locationsConfig: ObjectConfig<LocationsFormValue> = {
  ...aiProposalsConfig,
  rooms: { type: "list", update: "incremental", config: locationRowConfig },
};
